import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import { CalendarDays, CheckCircle2, CreditCard, Loader2, MapPin, Package, Truck, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { MobileSelect, MobileSelectItem } from "@/components/ui/mobile-select";
import { useToast } from "@/hooks/use-toast";
import { cn, formatKD } from "@/lib/utils";
import { useShopCart } from "@/lib/cart";
import { useAddresses, useCreateAddress } from "@/hooks/use-addresses";
import { useGovernorates, useDistricts, useDeliveryFee } from "@/hooks/use-locations";
import { useWalletBalance } from "@/hooks/use-wallet";
import { usePublicSettings } from "@/hooks/use-settings";
import { useCreateProductOrder } from "@/hooks/use-orders";


function SectionHeader({ icon: Icon, title }: { icon: typeof MapPin; title: string }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10">
        <Icon className="h-4.5 w-4.5 text-primary" />
      </span>
      <h2 className="text-lg font-bold font-display">{title}</h2>
    </div>
  );
}

export default function ShopCheckout() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const { items, subtotal, clearCart, b2bKey } = useShopCart();

  // ---- address
  const { data: addresses } = useAddresses();
  const [addressMode, setAddressMode] = useState<"saved" | "manual">("saved");
  const [selectedAddressId, setSelectedAddressId] = useState("");
  const [governorateId, setGovernorateId] = useState("");
  const [districtId, setDistrictId] = useState("");
  const [manual, setManual] = useState({ block: "", street: "", avenue: "", building: "", landmark: "" });
  const [saveAddress, setSaveAddress] = useState(false);
  const [addressLabel, setAddressLabel] = useState("");
  const { data: governorates, isLoading: governoratesLoading } = useGovernorates();
  const { data: districts, isLoading: districtsLoading } = useDistricts(governorateId || undefined);
  const createAddress = useCreateAddress();

  // No saved addresses → manual only
  useEffect(() => {
    if (addresses !== undefined && addresses.length === 0) setAddressMode("manual");
  }, [addresses]);

  // Same as booking: preselect the default (or first) saved address.
  useEffect(() => {
    if (addressMode === "saved" && addresses && addresses.length > 0 && !selectedAddressId) {
      const def = addresses.find((a) => a.isDefault) || addresses[0];
      if (def) setSelectedAddressId(def.id);
    }
  }, [addressMode, addresses, selectedAddressId]);

  const selectedAddress = addresses?.find((a) => a.id === selectedAddressId);
  useEffect(() => {
    if (addressMode === "saved" && selectedAddress) {
      setGovernorateId(String(selectedAddress.governorateId || ""));
      setDistrictId(String(selectedAddress.districtId || ""));
    }
  }, [addressMode, selectedAddress]);

  // ---- delivery day + period (admin-managed via public settings)
  const { data: settings } = usePublicSettings();
  const periods = (settings?.productDeliveryPeriods || []).filter((p) => p.active);
  const daysAhead = settings?.productDeliveryDaysAhead ?? 7;
  const dayOptions = useMemo(() => {
    const out: Array<{ value: string; label: string }> = [];
    for (let i = 1; i <= daysAhead; i++) {
      const d = new Date(Date.now() + i * 24 * 60 * 60 * 1000);
      const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      out.push({
        value,
        label: d.toLocaleDateString(isRtl ? "ar" : "en-US", { weekday: "short", day: "numeric", month: "short" }),
      });
    }
    return out;
  }, [daysAhead, isRtl]);
  const [deliveryDate, setDeliveryDate] = useState("");
  const [periodIndex, setPeriodIndex] = useState(0);
  useEffect(() => {
    if (!deliveryDate && dayOptions.length) setDeliveryDate(dayOptions[0].value);
  }, [dayOptions, deliveryDate]);

  // ---- totals + wallet
  const { data: deliveryFeeData } = useDeliveryFee(districtId || undefined);
  const deliveryFee = deliveryFeeData?.deliveryFee ?? 0;
  const totalPrice = subtotal + deliveryFee;
  const { data: walletData } = useWalletBalance();
  const walletBalance = walletData?.balance ?? 0;
  const [useWalletCredit, setUseWalletCredit] = useState(false);
  // Optional partial use of the wallet: empty = use as much as possible.
  const [customWalletAmount, setCustomWalletAmount] = useState("");
  const maxWalletCredit = Math.min(walletBalance, totalPrice);
  const parsedCustomAmount = parseFloat(customWalletAmount) || 0;
  const walletCreditUsed = useWalletCredit
    ? customWalletAmount.trim() !== ""
      ? Math.min(Math.max(0, parsedCustomAmount), maxWalletCredit)
      : maxWalletCredit
    : 0;
  const cashAmountDue = totalPrice - walletCreditUsed;

  // No gateway choice in the app: the customer picks KNET / card / Apple Pay
  // on the UPayments hosted page after placing the order.

  const createOrder = useCreateProductOrder();

  // Guard: empty cart → back to /cart (also fires right after a successful
  // submit clears the cart — the redirect below wins the race).
  useEffect(() => {
    if (items.length === 0 && !createOrder.isPending && !createOrder.isSuccess) {
      navigate("/cart");
    }
  }, [items.length, createOrder.isPending, createOrder.isSuccess, navigate]);

  const addressComplete =
    addressMode === "saved"
      ? !!selectedAddressId
      : !!(governorateId && districtId && manual.block && manual.street && manual.building);
  const canSubmit = items.length > 0 && addressComplete && !!deliveryDate && periods.length > 0;

  const submit = () => {
    const payload: import("@/lib/types").CreateProductOrderDto = {
      items: items.map((i) => ({ productId: i.productId, quantity: i.quantity })),
      ...(b2bKey ? { b2bKey } : {}),
      governorateId,
      districtId,
      deliveryDate,
      deliveryPeriodIndex: periodIndex,
      paymentMethod: cashAmountDue === 0 && useWalletCredit ? "wallet" : "online",
      ...(useWalletCredit ? { useWalletCredit: true, walletCreditAmount: walletCreditUsed } : {}),
    };
    if (addressMode === "saved") {
      payload.addressId = selectedAddressId;
    } else {
      payload.block = manual.block;
      payload.street = manual.street;
      if (manual.avenue) payload.avenue = manual.avenue;
      payload.building = manual.building;
      if (manual.landmark) payload.landmark = manual.landmark;
    }
    createOrder.mutate(payload, {
      onSuccess: (order) => {
        // Save the manual address for next time (best-effort, like booking).
        if (addressMode === "manual" && saveAddress && governorateId && districtId) {
          const district = districts?.find((d) => d.id === districtId);
          const defaultLabel = district ? (isRtl ? district.nameAr : district.nameEn) : t("addresses.defaultLabel");
          createAddress.mutate({
            label: addressLabel.trim() || defaultLabel,
            governorateId,
            districtId,
            block: manual.block.trim(),
            avenue: manual.avenue.trim() || undefined,
            street: manual.street.trim(),
            building: manual.building.trim(),
            landmark: manual.landmark.trim() || undefined,
            isDefault: !addresses || addresses.length === 0,
          });
        }
        clearCart();
        // Same success contract as the booking flow.
        if ((order.cashAmountDue ?? 0) === 0 && (order.walletCreditUsed ?? 0) > 0) {
          window.location.href = `/payment/success?orderId=${order.id}`;
        } else if (order.paymentLink) {
          window.location.href = order.paymentLink;
        } else {
          window.location.href = `/payment/success?orderId=${order.id}`;
        }
      },
      onError: (error: Error) => {
        toast({ title: t("shop.checkout.failed"), description: error.message, variant: "destructive" });
      },
    });
  };

  return (
    <div className="min-h-screen pt-24 pb-24">
      <div className="container mx-auto px-4 max-w-2xl space-y-5">
        <h1 className="text-3xl md:text-4xl font-bold font-display">{t("shop.checkout.title")}</h1>

        {/* items summary */}
        <Card className="rounded-2xl border border-border/50 p-5">
          <SectionHeader icon={Package} title={t("shop.checkout.items")} />
          <ul className="space-y-2">
            {items.map((item) => (
              <li key={item.productId} className="flex items-center justify-between gap-3 text-sm">
                <span className="min-w-0 truncate">
                  {isRtl ? item.nameAr : item.nameEn}
                  <span className="text-muted-foreground"> ×{item.quantity}</span>
                </span>
                <span className="font-mono">{formatKD(item.price * item.quantity)}</span>
              </li>
            ))}
          </ul>
          <div className="mt-3 border-t border-border pt-3 text-end">
            <Link href="/cart" className="text-xs text-primary hover:underline">
              {t("shop.checkout.editCart")}
            </Link>
          </div>
        </Card>

        {/* address — same layout as the booking page's location step */}
        <Card className="rounded-2xl border border-border/50 p-5">
          <SectionHeader icon={MapPin} title={t("shop.checkout.address")} />

          <div className="space-y-4">
            {(addresses?.length ?? 0) > 0 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    type="button"
                    variant={addressMode === "saved" ? "default" : "outline"}
                    size="sm"
                    className="rounded-full"
                    onClick={() => setAddressMode("saved")}
                    data-testid="button-use-saved"
                  >
                    {t("addresses.useSaved")}
                  </Button>
                  <Button
                    type="button"
                    variant={addressMode === "manual" ? "default" : "outline"}
                    size="sm"
                    className="rounded-full"
                    onClick={() => {
                      setAddressMode("manual");
                      setSelectedAddressId("");
                      setGovernorateId("");
                      setDistrictId("");
                    }}
                    data-testid="button-enter-manually"
                  >
                    {t("addresses.enterManually")}
                  </Button>
                </div>

                {addressMode === "saved" && (
                  <>
                    <MobileSelect
                      value={selectedAddressId || undefined}
                      onValueChange={(v) => v && setSelectedAddressId(v)}
                      placeholder={t("addresses.selectAddress")}
                      className="rounded-xl h-12"
                      data-testid="select-saved-address"
                    >
                      {(addresses || []).map((addr) => (
                        <MobileSelectItem key={addr.id} value={addr.id}>
                          <span className="flex items-center gap-2">
                            {addr.label}
                            {addr.isDefault && (
                              <span className="text-xs text-primary">({t("addresses.default")})</span>
                            )}
                          </span>
                        </MobileSelectItem>
                      ))}
                    </MobileSelect>

                    {selectedAddress && (() => {
                      const gov = selectedAddress.governorate || governorates?.find((g) => g.id === selectedAddress.governorateId);
                      const dist = selectedAddress.district || districts?.find((d) => d.id === selectedAddress.districtId);
                      const govName = isRtl ? gov?.nameAr : gov?.nameEn;
                      const distName = isRtl ? dist?.nameAr : dist?.nameEn;
                      return (
                        <div className="bg-muted/50 rounded-lg p-3 text-sm" data-testid="saved-address-summary">
                          <p className="font-medium">{selectedAddress.label}</p>
                          {(govName || distName) && (
                            <p className="text-muted-foreground">{[govName, distName].filter(Boolean).join(", ")}</p>
                          )}
                          <p className="text-muted-foreground">
                            {[
                              selectedAddress.block && `${t("addresses.block")} ${selectedAddress.block}`,
                              selectedAddress.street && `${t("addresses.street")} ${selectedAddress.street}`,
                              selectedAddress.avenue && `${t("addresses.avenue")} ${selectedAddress.avenue}`,
                              selectedAddress.building && `${t("addresses.building")} ${selectedAddress.building}`,
                            ].filter(Boolean).join(", ")}
                          </p>
                          {selectedAddress.landmark && (
                            <p className="text-muted-foreground text-xs">{t("addresses.landmark")}: {selectedAddress.landmark}</p>
                          )}
                        </div>
                      );
                    })()}
                  </>
                )}
              </div>
            )}

            {addressMode === "manual" && (
              <div className={cn("grid md:grid-cols-2 gap-4", isRtl && "md:grid-flow-dense")}>
                <div className="space-y-2">
                  <Label className="text-sm">{t("booking.governorate")}</Label>
                  <MobileSelect
                    value={governorateId}
                    onValueChange={(v) => {
                      setGovernorateId(v);
                      setDistrictId("");
                    }}
                    placeholder={t("booking.selectGovernorate")}
                    className={cn("rounded-xl h-12", isRtl && "text-right")}
                    data-testid="select-governorate"
                  >
                    {governoratesLoading ? (
                      <MobileSelectItem value="loading" disabled>{t("common.loading")}</MobileSelectItem>
                    ) : !governorates || governorates.length === 0 ? (
                      <MobileSelectItem value="no-governorates" disabled>{t("booking.noGovernorates")}</MobileSelectItem>
                    ) : (
                      governorates.map((g) => (
                        <MobileSelectItem key={g.id} value={g.id}>
                          {isRtl ? g.nameAr : g.nameEn}
                        </MobileSelectItem>
                      ))
                    )}
                  </MobileSelect>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">{t("booking.district")}</Label>
                  <MobileSelect
                    value={districtId}
                    onValueChange={setDistrictId}
                    disabled={!governorateId}
                    placeholder={governorateId ? t("booking.selectDistrict") : t("booking.selectGovernorateFirst")}
                    className={cn("rounded-xl h-12", isRtl && "text-right")}
                    data-testid="select-district"
                  >
                    {districtsLoading ? (
                      <MobileSelectItem value="loading" disabled>{t("common.loading")}</MobileSelectItem>
                    ) : !districts || districts.length === 0 ? (
                      <MobileSelectItem value="no-districts" disabled>{t("booking.noDistricts")}</MobileSelectItem>
                    ) : (
                      districts.map((d) => (
                        <MobileSelectItem key={d.id} value={d.id}>
                          {isRtl ? d.nameAr : d.nameEn}
                        </MobileSelectItem>
                      ))
                    )}
                  </MobileSelect>
                </div>
              </div>
            )}

            {districtId && deliveryFee > 0 && (
              <div className="flex items-center justify-between text-sm bg-muted/50 rounded-lg px-3 py-2">
                <span className="text-muted-foreground flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  {t("booking.deliveryFee")}
                </span>
                <span className="font-medium">{formatKD(deliveryFee)} {t("services.currency")}</span>
              </div>
            )}

            {addressMode === "manual" && (
              <div className="space-y-4 border-t border-border/50 pt-4">
                <div className={cn("grid md:grid-cols-4 gap-4", isRtl && "md:grid-flow-dense")}>
                  {([
                    ["block", true, "blockPlaceholder"],
                    ["street", true, "streetPlaceholder"],
                    ["avenue", false, "avenuePlaceholder"],
                    ["building", true, "buildingPlaceholder"],
                  ] as const).map(([key, required, ph]) => (
                    <div key={key} className="space-y-2">
                      <Label className="text-sm">
                        {t(`addresses.${key}`)}{required ? " *" : ""}
                      </Label>
                      <Input
                        placeholder={t(`addresses.${ph}`)}
                        value={manual[key]}
                        onChange={(e) => setManual((m) => ({ ...m, [key]: e.target.value }))}
                        className={cn("rounded-xl h-12", isRtl && "text-right")}
                        dir={isRtl ? "rtl" : "ltr"}
                        data-testid={`input-${key}`}
                      />
                    </div>
                  ))}
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">{t("addresses.landmark")}</Label>
                  <Input
                    placeholder={t("addresses.landmarkPlaceholder")}
                    value={manual.landmark}
                    onChange={(e) => setManual((m) => ({ ...m, landmark: e.target.value }))}
                    className={cn("rounded-xl h-12", isRtl && "text-right")}
                    dir={isRtl ? "rtl" : "ltr"}
                    data-testid="input-landmark"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox
                    id="shop-save-address"
                    checked={saveAddress}
                    onCheckedChange={(c) => setSaveAddress(c === true)}
                    data-testid="checkbox-save-address"
                  />
                  <label htmlFor="shop-save-address" className="text-sm cursor-pointer">
                    {t("addresses.saveForFuture")}
                  </label>
                </div>

                {saveAddress && (
                  <div className="space-y-2">
                    <Label className="text-sm">{t("addresses.labelOptional")}</Label>
                    <Input
                      placeholder={t("addresses.labelPlaceholder")}
                      value={addressLabel}
                      onChange={(e) => setAddressLabel(e.target.value)}
                      className={cn("rounded-xl h-12", isRtl && "text-right")}
                      dir={isRtl ? "rtl" : "ltr"}
                      data-testid="input-address-label"
                    />
                    <p className="text-xs text-muted-foreground">{t("addresses.labelDescription")}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </Card>

        {/* delivery day + period */}
        <Card className="rounded-2xl border border-border/50 p-5">
          <SectionHeader icon={CalendarDays} title={t("shop.checkout.deliveryDay")} />
          <div className="flex gap-2 overflow-x-auto pb-2 mb-4">
            {dayOptions.map((day) => (
              <button
                key={day.value}
                type="button"
                onClick={() => setDeliveryDate(day.value)}
                className={cn(
                  "shrink-0 rounded-xl border-2 px-4 py-2.5 text-sm font-medium transition-all whitespace-nowrap",
                  deliveryDate === day.value
                    ? "border-primary bg-primary/5 text-primary ring-4 ring-primary/10"
                    : "border-border hover:border-primary/50 text-muted-foreground",
                )}
                data-testid={`delivery-day-${day.value}`}
              >
                {day.label}
              </button>
            ))}
          </div>
          {periods.length > 0 ? (
            <div className="grid grid-cols-2 gap-2">
              {periods.map((period, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setPeriodIndex(i)}
                  className={cn(
                    "rounded-xl border-2 p-3 text-center transition-all",
                    periodIndex === i
                      ? "border-primary bg-primary/5 ring-4 ring-primary/10"
                      : "border-border hover:border-primary/50",
                  )}
                  data-testid={`delivery-period-${i}`}
                >
                  <span className={cn("block text-sm font-semibold", periodIndex === i ? "text-primary" : "")}>
                    {isRtl ? period.labelAr : period.labelEn}
                  </span>
                  <span className="block text-xs text-muted-foreground mt-0.5" dir="ltr">
                    {period.startTime}–{period.endTime}
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">{t("shop.checkout.noPeriods")}</p>
          )}
        </Card>

        {/* payment */}
        <Card className="rounded-2xl border border-border/50 p-5">
          <SectionHeader icon={CreditCard} title={t("shop.checkout.payment")} />

          {walletBalance > 0 && (
            <div className="mb-4 space-y-3">
              <div
                role="button"
                tabIndex={0}
                onClick={() => {
                  setUseWalletCredit(!useWalletCredit);
                  if (useWalletCredit) setCustomWalletAmount("");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setUseWalletCredit(!useWalletCredit);
                    if (useWalletCredit) setCustomWalletAmount("");
                  }
                }}
                className={cn(
                  "w-full flex items-center justify-between p-3 rounded-lg border transition-colors cursor-pointer",
                  useWalletCredit ? "border-primary bg-primary/5" : "border-border hover-elevate",
                )}
                data-testid="button-use-wallet-credit"
              >
                <div className="flex items-center gap-3">
                  <Wallet className={cn("w-5 h-5", useWalletCredit ? "text-primary" : "text-muted-foreground")} />
                  <div className="text-start">
                    <p className={cn("text-sm font-medium", useWalletCredit && "text-primary")}>
                      {t("booking.payment.useWalletCredit")}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t("booking.payment.walletBalance")}: {formatKD(walletBalance)} {t("services.currency")}
                    </p>
                  </div>
                </div>
                <div
                  className={cn(
                    "w-5 h-5 rounded border-2 flex items-center justify-center transition-colors",
                    useWalletCredit ? "bg-primary border-primary" : "border-muted-foreground/50",
                  )}
                  data-testid="checkbox-wallet-credit"
                >
                  {useWalletCredit && <CheckCircle2 className="w-4 h-4 text-primary-foreground" />}
                </div>
              </div>

              {useWalletCredit && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      step="0.001"
                      min="0"
                      max={maxWalletCredit}
                      placeholder={`${t("booking.payment.customAmount")} (${t("booking.payment.max")}: ${formatKD(maxWalletCredit)})`}
                      value={customWalletAmount}
                      onChange={(e) => setCustomWalletAmount(e.target.value)}
                      className="flex-1"
                      data-testid="input-wallet-amount"
                    />
                    <span className="text-sm text-muted-foreground whitespace-nowrap">{t("services.currency")}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{t("booking.payment.leaveEmptyForMax")}</p>
                </div>
              )}
            </div>
          )}

          {cashAmountDue === 0 && useWalletCredit ? (
            <div className="rounded-xl border border-green-300 bg-green-50 dark:border-green-800 dark:bg-green-900/20 p-4 text-sm text-green-700 dark:text-green-400">
              {t("shop.checkout.walletCoversAll")}
            </div>
          ) : (
            <div
              className="flex items-start gap-3 rounded-xl border border-border/60 bg-muted/20 p-3 text-sm text-muted-foreground"
              data-testid="payment-online-note"
            >
              <CreditCard className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
              <div>
                <p className="text-foreground">{t("shop.checkout.redirectNote", { amount: formatKD(cashAmountDue) })}</p>
                <p className="mt-1 text-xs">{t("shop.checkout.secureNote")}</p>
              </div>
            </div>
          )}
        </Card>

        {/* summary */}
        <Card className="rounded-2xl border border-border/50 p-5 space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">{t("shop.cart.subtotal")}</span>
            <span className="font-mono">{formatKD(subtotal)}</span>
          </div>
          <div className="flex justify-between">
            <span className="flex items-center gap-1.5 text-muted-foreground">
              <Truck className="h-3.5 w-3.5" />
              {t("booking.deliveryFee")}
            </span>
            <span className="font-mono">
              {districtId ? (deliveryFee > 0 ? formatKD(deliveryFee) : t("shop.checkout.freeDelivery")) : "—"}
            </span>
          </div>
          {walletCreditUsed > 0 && (
            <div className="flex justify-between text-green-600 dark:text-green-400">
              <span>{t("shop.checkout.walletCredit")}</span>
              <span className="font-mono">-{formatKD(walletCreditUsed)}</span>
            </div>
          )}
          <div className="flex justify-between border-t border-border pt-2 text-base font-bold">
            <span>{t("shop.checkout.total")}</span>
            <span className="text-primary">
              {formatKD(cashAmountDue)}{" "}
              <span className="text-xs text-muted-foreground font-normal">{t("services.currency")}</span>
            </span>
          </div>
        </Card>

        <Button
          className="w-full h-14 text-lg font-semibold rounded-xl sticky bottom-4 z-10 shadow-lg"
          disabled={!canSubmit || createOrder.isPending}
          onClick={submit}
          data-testid="checkout-submit"
        >
          {createOrder.isPending ? (
            <>
              <Loader2 className="w-5 h-5 me-2 animate-spin" />
              {t("booking.processing")}
            </>
          ) : (
            t("shop.checkout.placeOrder")
          )}
        </Button>
      </div>
    </div>
  );
}
